import { ChangeDetectorRef, Component, Injector, type OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import { getStyles } from './shop-cart-icon-view.component.style';
import type { ShopCartIconWidgetData } from '@web/widgets/shop-cart-icon/type/shop-cart-icon-widget.type';
import type { FormElementFormGroup } from '@common/types/form-element-forms.type';
import type { CartIconSettingsFormType } from '@web/widgets/shop-cart/type/shop-cart-widget.type';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { FormControl, FormGroup } from '@angular/forms';
import { WebBaseViewComponent } from '@web/widgets/common/abstract/web-base-view.component';
import { CART_ICON_TEMPLATES } from '@common/constants/cart-icon.const';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
import { BaseStylesService } from '@common/services/base-styles.service';
import { ESize } from '@common/enums';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

@Component({
    selector: 'web-shop-cart-icon-view',
    templateUrl: './shop-cart-icon-view.component.html',
    styleUrls: ['./shop-cart-icon-view.component.less'],
    imports: [CommonModule, CommonElementsModule, BsDropdownModule],
})
export class ShopCartIconViewComponent extends WebBaseViewComponent<ShopCartIconWidgetData> implements OnInit {
    protected readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    protected readonly stylesService: BaseStylesService = inject(BaseStylesService);
    protected readonly shopCartIconFacade = inject(ShopCartIconFacade);
    protected readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public itemForm: FormElementFormGroup | any;

    constructor() {
        super();
        this.itemForm = new FormGroup<CartIconSettingsFormType>({
            iconType: new FormControl(null),
            iconColor: new FormControl(null),
            iconBgColor: new FormControl(null),
            iconCounterColor: new FormControl(null),
            iconCounterBgColor: new FormControl(null),
            iconRounding: new FormControl(null),
            iconSize: new FormControl(null),
            iconPositionType: new FormControl(null),
        });
    }

    protected getStyles = getStyles;

    public ngOnInit(): void {
        this.shopCartIconFacade.cartIconData$.subscribe((data) => {
            this.itemForm.setValue(data);
            this.changeDetectorRef.detectChanges();
        });
    }

    public get iconType(): CartIconSettingsFormType['iconType'] {
        return this.itemForm.get('iconType') as CartIconSettingsFormType['iconType'];
    }

    public get iconColor(): CartIconSettingsFormType['iconColor'] {
        return this.itemForm.get('iconColor') as CartIconSettingsFormType['iconColor'];
    }

    public get iconBgColor(): CartIconSettingsFormType['iconBgColor'] {
        return this.itemForm.get('iconBgColor') as CartIconSettingsFormType['iconBgColor'];
    }

    public get iconCounterColor(): CartIconSettingsFormType['iconCounterColor'] {
        return this.itemForm.get('iconCounterColor') as CartIconSettingsFormType['iconCounterColor'];
    }

    public get iconCounterBgColor(): CartIconSettingsFormType['iconCounterBgColor'] {
        return this.itemForm.get('iconCounterBgColor') as CartIconSettingsFormType['iconCounterBgColor'];
    }

    public get iconRounding(): CartIconSettingsFormType['iconRounding'] {
        return this.itemForm.get('iconRounding') as CartIconSettingsFormType['iconRounding'];
    }

    public get iconSize(): CartIconSettingsFormType['iconSize'] {
        return this.itemForm.get('iconSize') as CartIconSettingsFormType['iconSize'];
    }

    public get iconPositionType(): CartIconSettingsFormType['iconPositionType'] {
        return this.itemForm.get('iconPositionType') as CartIconSettingsFormType['iconPositionType'];
    }

    public getIconStyles() {
        return {
            borderRadius: `${this.iconRounding.value}px`,
            background: this.iconBgColor.value,
            color: this.iconColor.value,
            height: this.previewIconSize(this.iconSize.value),
            width: this.previewIconSize(this.iconSize.value),
        };
    }

    public getCounterStyles() {
        return {
            background: this.iconCounterBgColor.value,
            color: this.iconCounterColor.value,
        };
    }

    public previewIconSize(size: ESize): string {
        switch (size) {
            case ESize.S:
                return '40px';
            case ESize.M:
                return '48px';
            case ESize.L:
                return '56px';
            default:
                return '48px';
        }
    }

    public previewIconSizeStyle() {
        return {
            height: `${this.previewIconImgSize(this.iconSize.value)}px`,
            width: `${this.previewIconImgSize(this.iconSize.value)}px`,
        };
    }

    public previewIconImgSize(size: ESize): number {
        switch (size) {
            case ESize.S:
                return 19;
            case ESize.M:
                return 24;
            case ESize.L:
                return 29;
            default:
                return 24;
        }
    }

    public getIcon(iconName: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(
            CART_ICON_TEMPLATES(iconName, this.previewIconImgSize(this.iconSize.value))
        );
    }
}
